/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useCallback } from 'react'
import { css } from '@emotion/react'
import { Modal } from 'antd'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { FilterDropdown } from '../ScheduleFilters'
import ReviewCalendarView from '../ReviewCalendarView'
import type { GeneratedGame } from '../GeneratedGamesSection'
import type { DivisionItem } from '../../hooks/useSchedulerDivisions'
import type { Venue } from '../../hooks/useSchedulerLocations'
import type { TimeSlot } from '../../hooks/useSchedulerTimeSlots'
import type { SchedulerConstraint } from '../../hooks/useSchedulerConstraints'

interface SchedulePreviewProps {
  games: GeneratedGame[]
  divisionId: string
  venues?: Venue[]
  divisions?: DivisionItem[]
  timeSlots?: TimeSlot[]
  constraints?: SchedulerConstraint[]
  competition?: { starts_at?: string; ends_at?: string } | null
}

type ViewMode = 'list' | 'day' | 'week' | 'month'

const VIEW_TABS: { key: ViewMode; label: string; icon: string }[] = [
  { key: 'list', label: 'List', icon: 'fa-list-ol' },
  { key: 'day', label: 'Day', icon: 'fa-calendar-day' },
  { key: 'week', label: 'Weekly', icon: 'fa-calendar-week' },
  { key: 'month', label: 'Monthly', icon: 'fa-calendar' },
]

const SchedulePreview: React.FC<SchedulePreviewProps> = ({
  games,
  divisionId,
  venues = [],
  divisions = [],
  timeSlots = [],
  constraints = [],
  competition = null,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [teamFilter, setTeamFilter] = useState<string[]>([])
  const [locationFilter, setLocationFilter] = useState<string[]>([])
  const [surfaceFilter, setSurfaceFilter] = useState<string[]>([])
  const [isFullscreen, setIsFullscreen] = useState(false)

  const divisionGames = useMemo(
    () => games.filter((g) => g.divisionId === divisionId),
    [games, divisionId]
  )

  // Build filter options
  const teamOptions = useMemo(() => {
    const teams = new Map<string, string>()
    divisionGames.forEach((g) => {
      if (g.homeTeamId) {
        teams.set(g.homeTeamId, g.homeTeam)
      }
      if (g.awayTeamId) {
        teams.set(g.awayTeamId, g.awayTeam)
      }
    })
    return Array.from(teams.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [divisionGames])

  const locationOptions = useMemo(() => {
    const locs = new Map<string, string>()
    divisionGames.forEach((g) => {
      if (g.locationId) {
        locs.set(g.locationId, g.location)
      }
    })
    return Array.from(locs.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [divisionGames])

  const surfaceOptions = useMemo(() => {
    const surfs = new Map<string, string>()
    divisionGames.forEach((g) => {
      if (g.surfaceId) {
        surfs.set(g.surfaceId, g.surface)
      }
    })
    return Array.from(surfs.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [divisionGames])

  // Apply all filters
  const filteredGames = useMemo(() => {
    let result = divisionGames
    if (teamFilter.length > 0) {
      result = result.filter(
        (g) =>
          teamFilter.includes(g.homeTeamId || '') ||
          teamFilter.includes(g.awayTeamId || '')
      )
    }
    if (locationFilter.length > 0) {
      result = result.filter((g) => locationFilter.includes(g.locationId || ''))
    }
    if (surfaceFilter.length > 0) {
      result = result.filter((g) => surfaceFilter.includes(g.surfaceId || ''))
    }
    return result
  }, [divisionGames, teamFilter, locationFilter, surfaceFilter])

  const noopSaveGame = useCallback(async () => true, [])

  const hasActiveFilters =
    teamFilter.length > 0 ||
    locationFilter.length > 0 ||
    surfaceFilter.length > 0

  const handleClearFilters = useCallback(() => {
    setTeamFilter([])
    setLocationFilter([])
    setSurfaceFilter([])
  }, [])

  // Empty state
  if (divisionGames.length === 0) {
    return (
      <div css={sectionContainer}>
        <div css={sectionHeader}>
          <div css={sectionTitleGroup}>
            <span css={sectionTitle}>SCHEDULE PREVIEW</span>
            <span css={sectionSubtitle}>
              Filter and review the generated schedule before publishing
            </span>
          </div>
        </div>
        <div css={emptyContainer}>
          <i className='fas fa-calendar-alt' css={emptyIcon} />
          <div css={emptyTitleStyle}>No Schedule Generated Yet</div>
          <div css={emptySubtitle}>
            Generate a schedule to see games for this division
          </div>
        </div>
      </div>
    )
  }

  const renderListTable = () => (
    <div css={tableContainer}>
      {/* Header */}
      <div css={tableHeader}>
        <span css={[headerCell, cellDate]}>DATE</span>
        <span css={[headerCell, cellTime]}>TIME</span>
        <span css={[headerCell, cellHome]}>HOME</span>
        <span css={[headerCell, cellAway]}>AWAY</span>
        <span css={[headerCell, cellLocation]}>LOCATION</span>
        <span css={[headerCell, cellSurface]}>SURFACE</span>
      </div>
      {/* Rows */}
      {filteredGames.map((game, index) => {
        const d = game.startsAt || game.date
        const t = game.startsAt || game.time
        return (
          <div key={game.id} css={tableRow(index % 2 === 0)}>
            <span css={[rowCell, cellDate]}>
              {d ? dayjs(d).format('ddd, MMM D, YYYY') : '-'}
            </span>
            <span css={[rowCell, cellTime]}>
              {t ? dayjs(t).format('h:mm A') : '-'}
            </span>
            <span css={[rowCell, cellHome]}>{game.homeTeam || '-'}</span>
            <span css={[rowCell, cellAway]}>{game.awayTeam || '-'}</span>
            <span css={[rowCell, cellLocation]}>{game.location || '-'}</span>
            <span css={[rowCell, cellSurface]}>{game.surface || '-'}</span>
          </div>
        )
      })}
    </div>
  )

  const renderFilters = () => (
    <div css={filtersRow}>
      <FilterDropdown
        label='Team'
        value={teamFilter}
        allLabel='All Teams'
        options={teamOptions}
        onChange={setTeamFilter}
      />
      <FilterDropdown
        label='Location'
        value={locationFilter}
        allLabel='All Locations'
        options={locationOptions}
        onChange={setLocationFilter}
      />
      <FilterDropdown
        label='Surface'
        value={surfaceFilter}
        allLabel='All Surfaces'
        options={surfaceOptions}
        onChange={setSurfaceFilter}
      />
      {hasActiveFilters && (
        <button css={clearFiltersBtn} onClick={handleClearFilters}>
          <i className='fas fa-times' css={clearFiltersIcon} />
          Clear Filters
        </button>
      )}
    </div>
  )

  const renderViewToggle = () => (
    <div css={viewToggleRow}>
      <div css={viewToggleGroup}>
        {VIEW_TABS.map((tab) => (
          <button
            key={tab.key}
            css={viewToggleBtn(viewMode === tab.key)}
            onClick={() => setViewMode(tab.key)}
          >
            <i className={`fas ${tab.icon}`} css={viewToggleIcon} />
            {tab.label}
          </button>
        ))}
      </div>
      <button
        css={fullscreenBtn}
        onClick={() => setIsFullscreen(!isFullscreen)}
        title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
      >
        <i
          className={`fas fa-${
            isFullscreen ? 'compress-arrows-alt' : 'expand-arrows-alt'
          }`}
        />
      </button>
    </div>
  )

  const renderBody = () =>
    viewMode === 'list' ? (
      renderListTable()
    ) : (
      <ReviewCalendarView
        viewMode={viewMode}
        games={filteredGames}
        venues={venues}
        divisions={divisions}
        timeSlots={timeSlots}
        constraints={constraints}
        competition={competition}
        onSaveGame={noopSaveGame}
        readOnly
      />
    )

  return (
    <div css={sectionContainer}>
      {/* Header */}
      <div css={sectionHeader}>
        <div css={sectionTitleGroup}>
          <span css={sectionTitle}>SCHEDULE PREVIEW</span>
          <span css={sectionSubtitle}>
            Filter and review the generated schedule before publishing
          </span>
        </div>
      </div>

      {/* Inline content */}
      {!isFullscreen && (
        <>
          {renderFilters()}
          {renderViewToggle()}
          {renderBody()}
        </>
      )}

      {/* Fullscreen modal */}
      <Modal
        open={isFullscreen}
        onCancel={() => setIsFullscreen(false)}
        width='100vw'
        footer={null}
        closable
        styles={{
          body: {
            height: '85vh',
            overflow: 'auto',
            background: colors.SECONDARY_400,
            padding: 24,
          },
          content: {
            background: colors.SECONDARY_400,
            padding: 0,
          },
          header: {
            background: colors.SECONDARY_400,
            borderBottom: `1px solid ${colors.SECONDARY_200}`,
            padding: '16px 24px',
          },
        }}
        style={{ top: 20, maxWidth: '100vw', padding: 0 }} // eslint-disable-line react-native/no-inline-styles
        title={<span css={modalTitleText}>SCHEDULE PREVIEW</span>}
      >
        {renderFilters()}
        {renderViewToggle()}
        {renderBody()}
      </Modal>
    </div>
  )
}

// --- Styles ---

const sectionContainer = css`
  margin-top: 24px;
`

const sectionHeader = css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

const sectionTitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1.68px;
  color: ${colors.WHITE};
  text-transform: uppercase;
`

const sectionTitleGroup = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const sectionSubtitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
  font-weight: 400;
  line-height: 17.4px;
`

const filtersRow = css`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 12px;
`

const clearFiltersBtn = css`
  display: flex;
  align-items: center;
  align-self: flex-end;
  gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_50};
  background: transparent;
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    color: ${colors.WHITE};
    border-color: ${colors.NEUTRAL_100};
  }
`

const clearFiltersIcon = css`
  font-size: 12px;
`

// --- Custom Table (matches Figma) ---

const tableContainer = css`
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const tableHeader = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 16px;
  background: ${colors.SECONDARY_300};
`

const headerCell = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_70};
  white-space: nowrap;
`

const tableRow = (isEven: boolean) => css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  background: ${isEven ? colors.SECONDARY_400 : 'transparent'};
`

const rowCell = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_30};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const cellDate = css`
  width: 132px;
  flex-shrink: 0;
`

const cellTime = css`
  width: 72px;
  flex-shrink: 0;
`

const cellHome = css`
  flex: 1 0 0;
  min-width: 0;
  text-align: right;
`

const cellAway = css`
  flex: 1 0 0;
  min-width: 0;
`

const cellLocation = css`
  width: 130px;
  flex-shrink: 0;
`

const cellSurface = css`
  width: 96px;
  flex-shrink: 0;
`

// --- View Toggle ---

const viewToggleRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`

const viewToggleGroup = css`
  display: flex;
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const viewToggleBtn = (active: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-weight: ${active ? 700 : 400};
  font-size: 14px;
  line-height: 20.3px;
  padding: 8px 16px;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${active ? colors.WHITE : colors.NEUTRAL_50};
  background: ${active ? colors.SECONDARY_300 : colors.SECONDARY_500};
  white-space: nowrap;

  &:hover {
    color: ${colors.WHITE};
  }
`

const viewToggleIcon = css`
  font-size: 16px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const fullscreenBtn = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 37px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 8px;
  color: ${colors.PRIMARY};
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 16px;

  &:hover {
    background: ${colors.PRIMARY}1A;
  }
`

const modalTitleText = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  color: ${colors.WHITE};
  text-transform: uppercase;
`

const emptyContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 24px;
  background-color: ${colors.SECONDARY_300};
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_200};
`

const emptyIcon = css`
  font-size: 32px;
  color: ${colors.NEUTRAL_200};
  margin-bottom: 12px;
`

const emptyTitleStyle = css`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.WHITE};
  margin-bottom: 4px;
`

const emptySubtitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${colors.NEUTRAL_100};
`

export default SchedulePreview
